import { COLOR_MODE } from '@ouster/webviz';
import {
  ColorPaletteName,
  ImageSettings,
} from '@ouster/webviz-react-components';

type ImagePaletteMemory = Record<
  string,
  Record<number, Record<string, ColorPaletteName>>
>;

export function setRgbPaletteOption(
  setting: ImageSettings,
  shouldIncludeRgb: boolean
): void {
  if (shouldIncludeRgb) {
    if (!setting.colorPaletteOptions.includes('RGB')) {
      setting.colorPaletteOptions = [...setting.colorPaletteOptions, 'RGB'];
    }
    return;
  }

  if (setting.colorPaletteOptions.includes('RGB')) {
    setting.colorPaletteOptions = setting.colorPaletteOptions.filter(
      (palette) => palette !== 'RGB'
    );
  }
}

export function setImageReturnsNoToggle(setting: ImageSettings): void {
  setting.returnsSettings = [{ visible: true, disabled: true }];
}

export function resolveReturnIndexFromColorMode(
  coloringMode: COLOR_MODE | string
): number {
  return String(coloringMode).endsWith('2') ? 1 : 0;
}

export const normalizeSelectedSensorImageSettingsForAvailableModes = ({
  selectedSensorSettingsArray,
  availableImageColorModes,
  selectedImagePanelSn,
  lastUserImagePaletteBySensorIdxAndMode,
}: {
  selectedSensorSettingsArray: ImageSettings[];
  availableImageColorModes: (COLOR_MODE | string)[];
  selectedImagePanelSn: string;
  lastUserImagePaletteBySensorIdxAndMode: ImagePaletteMemory;
}): void => {
  const allowedModes = availableImageColorModes ?? [];
  const allowedSet = new Set(allowedModes.map(String));

  const fallbackMode =
    allowedModes.find((m) => String(m).startsWith(COLOR_MODE.REFLECTIVITY)) ??
    allowedModes[0];

  if (!fallbackMode) {
    return;
  }

  selectedSensorSettingsArray.forEach((setting, index) => {
    const prevMode = setting.coloringMode;
    const prevPalette = setting.colorPalette;
    const rememberedPaletteForMode =
      lastUserImagePaletteBySensorIdxAndMode[selectedImagePanelSn]?.[index]?.[
        String(prevMode)
      ];

    const modeIsValid = prevMode && allowedSet.has(String(prevMode));
    if (modeIsValid) {
      const isReflectivity = String(prevMode).startsWith(
        COLOR_MODE.REFLECTIVITY
      );
      const paletteIsMissing = !prevPalette;
      const paletteIsDefault = prevPalette === 'Greyscale';

      if (
        isReflectivity &&
        !rememberedPaletteForMode &&
        (paletteIsMissing || paletteIsDefault)
      ) {
        setting.colorPalette = 'CalRef';
      }
      return;
    }

    setting.coloringMode = fallbackMode;

    const isReflectivity = String(fallbackMode).startsWith(
      COLOR_MODE.REFLECTIVITY
    );

    const paletteIsMissing = !prevPalette;
    const paletteIsDefault = prevPalette === 'Greyscale';

    if (
      isReflectivity &&
      !rememberedPaletteForMode &&
      (paletteIsMissing || paletteIsDefault)
    ) {
      setting.colorPalette = 'CalRef';
    }
  });
};

export const normalizeVisibleImageSettingsForDisplay = ({
  selectedVisibleSettings,
  rgbChannels,
}: {
  selectedVisibleSettings: ImageSettings[];
  rgbChannels: string[];
}): void => {
  selectedVisibleSettings.forEach((setting) => {
    if (rgbChannels.length > 0 && rgbChannels.includes(setting.coloringMode)) {
      setRgbPaletteOption(setting, true);
      setting.colorPalette = 'RGB';
      setting.isAutoLevelsOn = setting.isAutoLevelsOn ?? true;
      setting.isNoiseSuppressionOn = false;
      setting.isAutoLevelsDisabled = false;
      setting.isNoiseSuppressionDisabled = true;
    } else {
      setRgbPaletteOption(setting, false);
      if (setting.coloringMode?.startsWith(COLOR_MODE.REFLECTIVITY)) {
        if (setting.colorPalette === 'CalRef') {
          setting.isAutoLevelsDisabled = true;
          setting.isAutoLevelsOn = false;
          setting.isNoiseSuppressionDisabled = true;
          setting.isNoiseSuppressionOn = false;
        } else {
          setting.isAutoLevelsDisabled = false;
          setting.isAutoLevelsOn = setting.isAutoLevelsOn ?? true;
          setting.isNoiseSuppressionDisabled = true;
          setting.isNoiseSuppressionOn = false;
        }
      } else if (setting.coloringMode === String(COLOR_MODE.NEAR_IR)) {
        setting.isNoiseSuppressionDisabled = false;
      } else {
        setting.isNoiseSuppressionDisabled = true;
        setting.isNoiseSuppressionOn = false;
      }
    }
  });
};

export const normalizeSelectedSensorImageOptions = ({
  selectedSensorSettingsArray,
  availableImageColorModes,
}: {
  selectedSensorSettingsArray: ImageSettings[];
  availableImageColorModes: (COLOR_MODE | string)[];
}): void => {
  selectedSensorSettingsArray.forEach((setting) => {
    // Allow duplicates: do not exclude modes already used
    setting.colorModeOptions = availableImageColorModes;
    setImageReturnsNoToggle(setting);
  });
};
